// StoryReview — the review panel for one factory run's story. Shows the full
// shooting plan: the cast (등장인물 묘사 + 생성 캐릭터 매칭) and per-scene
// direction (연출: 대사/영상/카메라/동작/효과음), all editable, plus title
// rename and the stage actions. Overflow-safe (min-w-0 + wrapping) so long
// prompts never push the master-detail layout.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Check,
  ExternalLink,
  Film,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Play,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type {
  FactoryRun,
  FactoryStage,
  SceneClip,
  ShootingPlan,
  StoryCharacter,
  StoryScene,
  Video,
} from '@/api/types'
import { Badge, Button, Input, Select, Textarea } from '@/admin-design/_common'

export default function StoryReview({ run }: { run: FactoryRun }) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['factory-runs', run.config_id] })
    if (run.story_id) qc.invalidateQueries({ queryKey: ['story', run.story_id] })
  }

  const storyQ = useQuery({
    queryKey: ['story', run.story_id],
    queryFn: () => api.getStory(run.story_id as number),
    enabled: Boolean(run.story_id),
  })
  const charsQ = useQuery({ queryKey: ['characters'], queryFn: api.listCharacters })
  const stylesQ = useQuery({ queryKey: ['styles'], queryFn: api.listStyles })
  // The render for this run's story — the latest video row. Appears as soon as
  // rendering starts (RenderScenes creates it), so the scene panel is visible
  // right away rather than waiting on the factory tick to set run.video_id.
  const videosQ = useQuery({
    queryKey: ['videos'],
    queryFn: api.listVideos,
    refetchInterval: 4000,
  })

  const approve = useMutation({
    mutationFn: () => api.approveFactoryRun(run.id),
    onSuccess: invalidate,
  })
  const reject = useMutation({
    mutationFn: () => api.rejectFactoryRun(run.id),
    onSuccess: invalidate,
  })
  const savePlan = useMutation({
    mutationFn: (plan: ShootingPlan) => api.updateStoryPlan(run.story_id as number, plan),
    onSuccess: invalidate,
  })
  const actionErr = approve.isError
    ? errorMessage(approve.error)
    : reject.isError
      ? errorMessage(reject.error)
      : savePlan.isError
        ? errorMessage(savePlan.error)
        : null

  const plan = storyQ.data?.shooting_plan
  const scenes = plan?.scenes ?? []
  const cast = plan?.characters ?? []
  const readyChars = (charsQ.data ?? []).filter((c) => c.status === 'ready')
  const storyVideo: Video | undefined = (videosQ.data ?? [])
    .filter((v) => v.story_id === run.story_id)
    .sort((a, b) => b.id - a.id)[0]

  const patch = (next: ShootingPlan) => savePlan.mutate(next)
  const matchCharacter = (charId: string, characterId?: number) => {
    if (!plan) return
    patch({
      ...plan,
      characters: plan.characters.map((c) =>
        c.id === charId ? { ...c, character_id: characterId } : c,
      ),
    })
  }
  const saveScene = (scene: StoryScene) => {
    if (!plan) return
    patch({ ...plan, scenes: plan.scenes.map((s) => (s.id === scene.id ? scene : s)) })
  }

  const [genModel, setGenModel] = useState('')
  const [fullImg, setFullImg] = useState<string | null>(null)
  const styles = stylesQ.data ?? []
  const genModelId = genModel || styles[0]?.id || ''
  // Generate a character image straight from the persona description using the
  // chosen model, then auto-match it to this cast member (GPU is local/free).
  const genChar = useMutation({
    mutationFn: (ch: StoryCharacter) =>
      api.createCharacter({
        style_id: genModelId,
        subject_prompt: (ch.persona || ch.krea2_prompt || '').trim(),
        aspect: 'portrait',
      }),
    onSuccess: (created, ch) => {
      qc.invalidateQueries({ queryKey: ['characters'] })
      matchCharacter(ch.id, created.id)
    },
  })

  const [titleDraft, setTitleDraft] = useState<string | null>(null)
  const [sceneDraft, setSceneDraft] = useState<StoryScene | null>(null)
  const title = plan?.title ?? storyQ.data?.concept ?? ''

  return (
    <div className="min-w-0 rounded-md border border-border bg-card p-3">
      {!run.story_id ? (
        <p className="text-xs text-muted-foreground">
          대본 생성 중… 스토리가 준비되면 여기서 검토할 수 있어요.
        </p>
      ) : storyQ.isLoading ? (
        <p className="text-xs text-muted-foreground">스토리 불러오는 중…</p>
      ) : scenes.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {storyQ.data?.status === 'error'
            ? `스토리 실패: ${storyQ.data?.error}`
            : '스토리(샷플랜) 생성 중…'}
        </p>
      ) : (
        <div className="min-w-0 space-y-3">
          {/* Title (rename) */}
          <div className="flex min-w-0 items-center gap-1.5">
            {titleDraft === null ? (
              <>
                <div className="min-w-0 flex-1 break-words text-xs font-semibold text-card-foreground">
                  {title}
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    · {scenes.length}씬
                  </span>
                </div>
                {plan && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 shrink-0 px-1.5 text-[11px]"
                    onClick={() => setTitleDraft(title)}
                  >
                    <Pencil size={12} /> 제목
                  </Button>
                )}
              </>
            ) : (
              <>
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  className="h-7 min-w-0 flex-1 text-xs"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="h-7 shrink-0 px-2"
                  loading={savePlan.isPending}
                  onClick={() => {
                    if (plan && titleDraft.trim()) patch({ ...plan, title: titleDraft.trim() })
                    setTitleDraft(null)
                  }}
                >
                  저장
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-1.5"
                  onClick={() => setTitleDraft(null)}
                >
                  <X size={13} />
                </Button>
              </>
            )}
          </div>

          {/* Cast: description + match to a generated character */}
          {cast.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-normal text-muted-foreground">
                  등장인물 {cast.length}
                </span>
                {styles.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">모델</span>
                    <Select
                      value={genModelId}
                      onValueChange={setGenModel}
                      options={styles.map((s) => ({ label: `${s.name} · ${s.kind}`, value: s.id }))}
                      className="h-7 max-w-[190px] text-[11px]"
                      aria-label="이미지 생성 모델"
                    />
                  </div>
                )}
              </div>
              {genChar.isError && (
                <p className="text-[10px] text-destructive">
                  생성 실패: {errorMessage(genChar.error)}
                </p>
              )}
              {cast.map((ch) => {
                const matched = readyChars.find((c) => c.id === ch.character_id)
                return (
                  <div key={ch.id} className="min-w-0 rounded border border-border bg-muted/20 p-2">
                    <div className="flex min-w-0 gap-3">
                      {/* Left: photo (big) + character picker below it */}
                      <div className="flex w-28 shrink-0 flex-col gap-1.5">
                        {matched?.image_path ? (
                          <button
                            type="button"
                            onClick={() => setFullImg(matched.image_path ?? null)}
                            title="크게 보기"
                            className="block w-28 cursor-zoom-in overflow-hidden rounded-md border border-border"
                          >
                            <img
                              src={matched.image_path}
                              alt={matched.name}
                              className="aspect-[3/4] w-28 object-cover"
                            />
                          </button>
                        ) : (
                          <div className="flex aspect-[3/4] w-28 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border bg-muted/40 text-muted-foreground">
                            <ImageIcon size={22} aria-hidden="true" />
                            <span className="text-[9px]">이미지 없음</span>
                          </div>
                        )}
                        {readyChars.length === 0 ? (
                          <span className="text-center text-[9px] text-muted-foreground">
                            캐릭터 없음
                          </span>
                        ) : (
                          <Select
                            value={ch.character_id ? String(ch.character_id) : ''}
                            onValueChange={(v) => matchCharacter(ch.id, v ? Number(v) : undefined)}
                            options={[
                              { label: '— 매칭 안 함 —', value: '' },
                              ...readyChars.map((c) => ({
                                label: c.name || `#${c.id}`,
                                value: String(c.id),
                              })),
                            ]}
                            className="h-7 w-28 text-[11px]"
                            aria-label={`${ch.id} 캐릭터 매칭`}
                          />
                        )}
                      </div>
                      {/* Right: persona description */}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <Badge variant="info" className="shrink-0 rounded text-[10px]">
                            {ch.id}
                          </Badge>
                          {matched && (
                            <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">
                              → {matched.name || `#${matched.id}`}
                            </span>
                          )}
                        </div>
                        <p className="break-words text-[11px] text-card-foreground">
                          {ch.persona || ch.krea2_prompt}
                        </p>
                        {ch.persona && ch.krea2_prompt && (
                          <p className="break-words text-[10px] text-muted-foreground">
                            🎨 {ch.krea2_prompt}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-6 px-1.5 text-[11px]"
                            loading={genChar.isPending && genChar.variables?.id === ch.id}
                            disabled={!genModelId || !(ch.persona || ch.krea2_prompt)}
                            onClick={() => genChar.mutate(ch)}
                            title="묘사로 바로 이미지 생성 후 자동 매칭"
                          >
                            <Sparkles size={11} /> 묘사로 생성
                          </Button>
                          <a
                            href={`/characters?prompt=${encodeURIComponent(ch.persona || ch.krea2_prompt || '')}`}
                            target="_blank"
                            rel="noreferrer"
                            title="캐릭터 생성 페이지에서 세부설정"
                            className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-1.5 text-[11px] text-muted-foreground hover:border-primary/50 hover:text-foreground"
                          >
                            <ExternalLink size={11} /> 세부설정
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Scenes: full per-scene direction (editable) */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-normal text-muted-foreground">
              씬 연출 {scenes.length}
            </div>
            <ol className="space-y-1.5">
              {scenes.map((s) =>
                sceneDraft?.id === s.id ? (
                  <li
                    key={s.id}
                    className="min-w-0 space-y-1.5 rounded border border-primary/40 bg-muted/20 p-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <Badge variant="muted" className="shrink-0 rounded text-[10px]">
                        #{s.id}
                      </Badge>
                      <Input
                        value={sceneDraft.character_ref}
                        onChange={(e) =>
                          setSceneDraft({ ...sceneDraft, character_ref: e.target.value })
                        }
                        placeholder="등장인물 id"
                        className="h-7 w-28 text-[11px]"
                      />
                      <Input
                        type="number"
                        min={1}
                        value={sceneDraft.length_s}
                        onChange={(e) =>
                          setSceneDraft({ ...sceneDraft, length_s: Number(e.target.value) })
                        }
                        className="h-7 w-16 text-[11px]"
                      />
                      <span className="text-[10px] text-muted-foreground">초</span>
                    </div>
                    <SceneField
                      label="🗣 대사"
                      value={sceneDraft.voice}
                      onChange={(v) => setSceneDraft({ ...sceneDraft, voice: v })}
                      rows={2}
                    />
                    <SceneField
                      label="🎬 영상"
                      value={sceneDraft.visual}
                      onChange={(v) => setSceneDraft({ ...sceneDraft, visual: v })}
                      rows={2}
                    />
                    <SceneField
                      label="🎭 동작"
                      value={sceneDraft.action}
                      onChange={(v) => setSceneDraft({ ...sceneDraft, action: v })}
                      rows={1}
                    />
                    <SceneField
                      label="📷 카메라"
                      value={sceneDraft.camera}
                      onChange={(v) => setSceneDraft({ ...sceneDraft, camera: v })}
                      rows={1}
                    />
                    <SceneField
                      label="🔊 효과음"
                      value={sceneDraft.sounds}
                      onChange={(v) => setSceneDraft({ ...sceneDraft, sounds: v })}
                      rows={1}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="h-7 px-2"
                        loading={savePlan.isPending}
                        onClick={() => {
                          saveScene(sceneDraft)
                          setSceneDraft(null)
                        }}
                      >
                        저장
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => setSceneDraft(null)}
                      >
                        취소
                      </Button>
                    </div>
                  </li>
                ) : (
                  <li key={s.id} className="min-w-0 rounded border border-border bg-muted/20 p-2">
                    <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1.5">
                      <Badge variant="muted" className="shrink-0 rounded text-[10px]">
                        #{s.id}
                      </Badge>
                      {s.character_ref && (
                        <Badge variant="info" className="shrink-0 rounded text-[10px]">
                          {s.character_ref}
                        </Badge>
                      )}
                      <span className="text-[11px] text-muted-foreground">~{s.length_s}s</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-6 shrink-0 px-1.5 text-[11px]"
                        onClick={() => setSceneDraft(s)}
                      >
                        <Pencil size={12} /> 수정
                      </Button>
                    </div>
                    {s.voice && (
                      <p className="break-words text-[12px] text-card-foreground">🗣 {s.voice}</p>
                    )}
                    {s.visual && (
                      <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
                        🎬 {s.visual}
                      </p>
                    )}
                    {s.action && (
                      <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
                        🎭 {s.action}
                      </p>
                    )}
                    {s.camera && (
                      <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
                        📷 {s.camera}
                      </p>
                    )}
                    {s.sounds && (
                      <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
                        🔊 {s.sounds}
                      </p>
                    )}
                  </li>
                ),
              )}
            </ol>
          </div>
        </div>
      )}

      {storyVideo ? <SceneClipsPanel videoId={storyVideo.id} configId={run.config_id} /> : null}

      {/* Actions */}
      <div className="mt-3 space-y-1 border-t border-border pt-2">
        <div className="flex flex-wrap items-center gap-2">
          {run.stage === 'awaiting_review' && (
            <>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => approve.mutate()}
                loading={approve.isPending}
              >
                <Play size={13} /> 진행 · 씬별 렌더 시작
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => reject.mutate()}
                disabled={reject.isPending}
              >
                <X size={13} /> 반려
              </Button>
            </>
          )}
          {run.stage === 'done' && run.video_id && (
            <>
              <a
                href={`/media/videos/${run.video_id}.mp4`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-7 items-center gap-1 rounded-md bg-secondary px-2 text-xs font-medium text-secondary-foreground hover:bg-secondary/80"
              >
                <Film size={13} /> 영상 보기
              </a>
              <Link
                to="/publish"
                className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Check size={13} /> 게시하러 가기
              </Link>
            </>
          )}
          {run.stage !== 'awaiting_review' && run.stage !== 'done' && (
            <span className="text-[11px] text-muted-foreground">
              {stageMessage(run.stage, run.error)}
            </span>
          )}
        </div>
        {run.stage === 'awaiting_review' && (
          <p className="text-[10px] text-muted-foreground">
            누르면 씬별로 렌더링을 시작합니다. 완료 후 각 씬 미리보기·재생성 버튼과 [검수 후
            합치기]가 위에 나타납니다.
          </p>
        )}
      </div>
      {actionErr && <p className="mt-1 break-words text-xs text-destructive">{actionErr}</p>}

      {fullImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setFullImg(null)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={fullImg}
            alt="전체 보기"
            className="max-h-full max-w-full rounded-md object-contain"
          />
          <button
            type="button"
            onClick={() => setFullImg(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="닫기"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  )
}

// SceneClipsPanel — per-scene render clips for a run's video. Each clip renders
// independently; the operator reviews, re-renders any scene, then merges the
// ready clips into the final video (검수 후 합치기 — no auto-concat).
function SceneClipsPanel({ videoId, configId }: { videoId: number; configId: number }) {
  const qc = useQueryClient()
  const clipsQ = useQuery({
    queryKey: ['scene-clips', videoId],
    queryFn: () => api.listSceneClips(videoId),
    refetchInterval: (q) =>
      (q.state.data as SceneClip[] | undefined)?.some((c) => c.status === 'generating')
        ? 3000
        : false,
  })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['scene-clips', videoId] })
    qc.invalidateQueries({ queryKey: ['factory-runs', configId] })
    qc.invalidateQueries({ queryKey: ['videos'] })
  }
  const rerender = useMutation({
    mutationFn: (idx: number) => api.renderScene(videoId, idx),
    onSuccess: refresh,
  })
  const merge = useMutation({ mutationFn: () => api.mergeVideo(videoId), onSuccess: refresh })
  const cancel = useMutation({ mutationFn: () => api.cancelRender(videoId), onSuccess: refresh })

  const clips = clipsQ.data ?? []
  if (clips.length === 0) return null
  const readyCount = clips.filter((c) => c.status === 'ready').length
  const allReady = readyCount === clips.length
  const anyGenerating = clips.some((c) => c.status === 'generating')

  return (
    <div className="mt-3 space-y-1.5 border-t border-border pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-normal text-muted-foreground">
          씬 클립 {readyCount}/{clips.length} 준비
        </span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="h-7 px-2"
          disabled={!allReady || merge.isPending}
          loading={merge.isPending}
          onClick={() => merge.mutate()}
          title={allReady ? '검수 완료 → 합치기' : '모든 씬이 준비되면 활성화'}
        >
          <Film size={13} /> 검수 후 합치기
        </Button>
        {anyGenerating && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-7 px-2"
            loading={cancel.isPending}
            onClick={() => cancel.mutate()}
            title="진행 중인 씬 렌더를 취소"
          >
            <X size={13} /> 렌더 취소
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {clips.map((c) => (
          <div
            key={c.id}
            className="min-w-0 space-y-1 rounded border border-border bg-muted/20 p-1"
          >
            <div className="flex items-center justify-between gap-1">
              <Badge variant="muted" className="rounded text-[10px]">
                씬 {c.idx + 1}
              </Badge>
              <Badge
                variant={
                  c.status === 'ready' ? 'success' : c.status === 'error' ? 'destructive' : 'info'
                }
                className="rounded text-[10px]"
              >
                {c.status}
              </Badge>
            </div>
            {c.status === 'ready' && c.clip_path ? (
              <video
                src={c.clip_path}
                controls
                className="aspect-video w-full rounded bg-black object-contain"
              />
            ) : c.status === 'error' ? (
              <div className="grid aspect-video place-items-center rounded bg-muted px-1 text-center text-[9px] text-destructive">
                실패{c.error ? `: ${c.error}` : ''}
              </div>
            ) : (
              <div className="grid aspect-video place-items-center rounded bg-muted text-muted-foreground">
                <Loader2 size={18} className="animate-spin" aria-hidden="true" />
              </div>
            )}
            {c.status === 'generating' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-full px-1 text-[10px]"
                loading={cancel.isPending}
                onClick={() => cancel.mutate()}
                title="이 씬 렌더 취소"
              >
                <X size={11} /> 취소
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-full px-1 text-[10px]"
                disabled={rerender.isPending}
                onClick={() => rerender.mutate(c.idx)}
                title="이 씬만 다시 생성"
              >
                <RefreshCw size={11} /> 재생성
              </Button>
            )}
          </div>
        ))}
      </div>
      {(merge.isError || rerender.isError || cancel.isError) && (
        <p className="text-[10px] text-destructive">
          {errorMessage(merge.error ?? rerender.error ?? cancel.error)}
        </p>
      )}
    </div>
  )
}

function SceneField({
  label,
  value,
  onChange,
  rows,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows: number
}) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 text-[10px] text-muted-foreground">{label}</div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full text-[11px] leading-snug"
      />
    </div>
  )
}

function stageMessage(stage: FactoryStage, error?: string): string {
  switch (stage) {
    case 'generating_script':
      return '대본 생성 중…'
    case 'generating_story':
      return '스토리(샷플랜) 생성 중…'
    case 'awaiting_render':
      return '씬 렌더 시작 대기 중… (곧 씬별 렌더링이 시작됩니다)'
    case 'rendering':
      return '씬별 렌더링 중… (위 클립에서 진행 상황을 확인하세요)'
    case 'awaiting_merge':
      return '씬 렌더 완료 — 위에서 검수 후 [검수 후 합치기]를 누르세요'
    case 'cancelled':
      return '취소됨'
    case 'error':
      return `실패${error ? `: ${error}` : ''}`
    default:
      return `진행 중: ${stage}`
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
